<template>
  <client-only>
    <div :class="['design-picker relative', { 'is-sliding': isSliding, 'design-picker-women': isWomenProductGroup, 'is-disabled': disableSlider, [customSelectClass]: !!customSelectClass } ]">
      <VueSlickCarousel
        v-if="designs.length"
        ref="carousel"
        v-bind="sliderOptions"
        @beforeChange="handleBeforeChange"
        @afterChange="handleAfterChange"
      >
        <div
          v-for="design in designs"
          :key="`${design.reference}-${design.productGroup}`"
          class="px-rem"
        >
          <div class="design-picker-container overflow-hidden">
            <div class="design-picker-container-shadow" />
            <div class="design-picker-container-body px-14 pb-7">
              <OImage
                class="relative"
                v-bind="getDesignImage(design.reference)"
                img-class="pointer-events-none"
                :alt="$t('Accessibilty.AltTags.PhotoOfTheDesignWithName', { name: design.name })"
              />
            </div>
            <div v-if="getShowIsFeatured(design)" class="design-picker-container-is-featured-label">
              <span class="flex items-center bg-black pl-rem pr-3 py-rem is-featured-label">
                <OSvg svg="halloween" class="mr-2" />
                <span class="text-sm font-body font-bold text-white" v-text="$t('FeaturedBoxersCampaign.SignUpBoxershortBadge')" />
              </span>
            </div>
            <div
              :class="[
                'design-picker-footer relative transition-all duration-300 flex flex-row justify-between items-center overflow-hidden', {
                  'bg-green-400 text-white border-t border-green-400': isActive(design.reference),
                  'border-t border-neutral-200 text-black': !isActive(design.reference)
                }
              ]"
            >
              <div class="font-display font-bold text-md">
                {{ design.name }}
              </div>
              <div
                class="ml-auto font-body text-xs flex flex-row items-center"
              >
                {{ isActive(design.reference) ? $t('SignUp.StepTwo.Selected') : $t('SignUp.StepTwo.Select') }}
                <div :class="['ml-2 rounded-full p-1', { 'bg-white': isActive(design.reference), 'bg-neutral-200': !isActive(design.reference) }]">
                  <OSvg :class="['w-3', { 'fill-green-400': isActive(design.reference), 'fill-neutral-00': !isActive(design.reference) }]" svg="checkmark" />
                </div>
              </div>
            </div>
            <div class="design-picker-container-cloak absolute top-0 left-0 right-0 bottom-0 pointer-events-none" />
          </div>
        </div>

        <template #customPaging="page">
          <div
            class="custom-dots-button focus:outline-none relative thumbnail-button cursor-pointer"
            role="button"
            tabindex="-1"
            :aria-label="designs[page] ? $t('Accessibilty.AriaLabels.PickDesign') : ''"
            @click="onPaginationClick($event, page)"
          >
            <div
              v-if="designs[page]"
            >
              <OImage
                class="relative"
                v-bind="thumbnailDesignImage(designs[page].reference)"
                img-class="pointer-events-none"
                :alt="$t('Accessibilty.AltTags.PhotoOfTheDesignWithName', { name: designs[page].name })"
              />
            </div>
            <transition name="scale">
              <div
                v-if="designs[page] && isActive(designs[page].reference)"
                :class="['ml-2 rounded-full is-active-badge relative bg-green-400']"
              >
                <OSvg :class="['w-3 fill-white']" svg="checkmark" />
              </div>
            </transition>
          </div>
        </template>

        <template #prevArrow="">
          <button
            class="absolute rounded-full bg-black h-10 w-10 focus:outline-none hover:bg-neutral-800 focus:bg-neutral-800 transition-colors duration-150 arrow-previous z-10"
            :aria-label="$t('Accessibilty.AriaLabels.PreviousDesign')"
            :disabled="disableSlider"
            tabindex="0"
          >
            <OSvg class="w-5 absolute fill-white" svg="custom-arrow-left" aria-hidden="true" />
          </button>
        </template>
        <template #nextArrow="">
          <button
            class="absolute rounded-full bg-black h-10 w-10 focus:outline-none hover:bg-neutral-800 focus:bg-neutral-800 transition-colors duration-150 arrow-next z-10"
            :aria-label="$t('Accessibilty.AriaLabels.NextDesign')"
            :disabled="disableSlider"
            tabindex="0"
          >
            <OSvg class="w-5 absolute fill-white" svg="custom-arrow-right" aria-hidden="true" />
          </button>
        </template>
      </VueSlickCarousel>
      <div class="px-4 flex items-center pt-1">
        <client-only>
          <div class="flex-grow mr-4">
            <div v-if="showFillBar" class="h-1 bg-neutral-200 rounded-full overflow-hidden">
              <div ref="fillBar" class="h-full filler-bar w-0 bg-black transition-all duration-200" />
            </div>
          </div>
          <div>
            <span class="font-body text-xs text-gray-500 opacity-50" v-text="$t('SignUp.OnePage.AmountOfDesigns', { amount: designs.length })" />
          </div>
        </client-only>
      </div>
      <div v-if="disableSlider" class="design-picker-disabled-notice flex items-center justify-center">
        <div class="design-picker-disabled-notice-box text-neutral-800">
          <div class="flex items-center justify-center">
            <div class="mr-3">
              <OSvg class="w-10 fill-neutral-800" svg="info-solid" />
            </div>
            <div class="mr-2">
              <p class="text-sm font-body font-bold" v-html="$t('SignUp.OnePage.DesignPickerDisabled.Title')" />
            </div>
            <div class="flex-shrink-0 hidden md:block">
              <NewButton class="disabled-button" color="white" @click="$emit('disabledClick')">
                {{ $t('SignUp.OnePage.DesignPickerDisabled.CTA') }}
              </NewButton>
            </div>
          </div>
          <div class="flex-shrink-0 text-center pt-3 md:hidden">
            <NewButton class="disabled-button" color="white" @click="$emit('disabledClick')">
              {{ $t('SignUp.OnePage.DesignPickerDisabled.CTA') }}
            </NewButton>
          </div>
        </div>
      </div>
    </div>
  </client-only>
</template>

<script>
import { getImagePaths } from '@/utils/general'
import OImage from '@/components/OImage'
import NewButton from '@/components/NewButton'
import OSvg from '@/components/OSvg'
import VueSlickCarousel from 'vue-slick-carousel'
import 'vue-slick-carousel/dist/vue-slick-carousel.css'
import { Orientations } from '@/enums/products'
import { vueWindowSizeMixin } from 'vue-window-size'
import { ScreenBreakpoints } from '@/enums/general'

export default {
  name: 'DesignPicker',
  components: {
    VueSlickCarousel,
    OImage,
    OSvg,
    NewButton
  },
  mixins: [vueWindowSizeMixin],
  props: {
    designs: {
      type: Array,
      required: true,
      validator: value => value.every(obj => ['reference', 'name'].every(prop => obj.hasOwnProperty(prop)))
    },
    selectedDesignReference: {
      type: String,
      required: true
    },
    imagePostfix: {
      type: String,
      default: ''
    },
    productGroup: {
      type: String,
      required: true
    },
    productGroupReference: {
      type: String,
      default: ''
    },
    customSelectClass: {
      type: String,
      default: ''
    },
    disableSlider: {
      type: Boolean,
      default: false
    },
    showIsFeatured: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isSliding: false
    }
  },
  computed: {
    sliderOptions () {
      return {
        arrows: !this.disableSlider,
        dots: true,
        centerMode: true,
        centerPadding: this.centerPadding,
        slidesToShow: 1,
        infinite: true,
        draggable: !this.disableSlider
      }
    },
    centerPadding () {
      if (this.isWomenProductGroup && this.isMediumScreen) {
        return '140px'
      }

      return '24px'
    },
    isWomenProductGroup () {
      return [Orientations.womenbralette, Orientations.womenhipster].includes(this.productGroupReference)
    },
    isMediumScreen () {
      return this.windowWidth >= ScreenBreakpoints.md
    },
    showFillBar () {
      return this.designs.length > (this.isMediumScreen ? 8 : 5)
    },
    customDotsSelector () {
      return this.customSelectClass ? `.${this.customSelectClass} .slick-dots` : '.slick-dots'
    }
  },
  mounted () {
    this.$nextTick(() => {
      const dotsContainer = this.$el.querySelector(this.customDotsSelector)
      if (dotsContainer) {
        this.updateFillBar(dotsContainer)
        dotsContainer.addEventListener('scroll', () => this.updateFillBar(dotsContainer))
      }
    })
  },
  beforeDestroy () {
    const dotsContainer = this.$el.querySelector(this.customDotsSelector)
    if (dotsContainer) {
      dotsContainer.removeEventListener('scroll', () => this.updateFillBar(dotsContainer))
    }
  },
  methods: {
    handleBeforeChange () {
      this.isSliding = true
    },
    getShowIsFeatured (design) {
      return design && design.isFeatured && this.showIsFeatured
    },
    onPaginationClick (event, page) {
      if (this.disableSlider) { return }

      this.scrollThumbnailToLeft(event)

      const design = this.designs[page] || this.designs[0]

      if (design) {
        this.$emit('select', design)
      }
    },
    handleAfterChange (currentSlide) {
      this.isSliding = false
      const design = this.designs[currentSlide] || this.designs[0]

      try {
        const dotsContainer = this.$el.querySelector(this.customDotsSelector)
        if (dotsContainer) {
          const activeButton = dotsContainer.querySelector(`li:nth-child(${currentSlide + 1}) .custom-dots-button`)
          if (activeButton) {
            this.scrollThumbnailToLeft({ currentTarget: activeButton })
          }
        }
      } catch (_) {}

      if (design) {
        this.$emit('select', design)
      }
    },
    scrollThumbnailToLeft (event) {
      try {
        const button = event.currentTarget
        const dotsContainer = button.closest(this.customDotsSelector)
        if (dotsContainer) {
          const firstThumbnail = dotsContainer.querySelector('li')
          const thumbnailWidth = firstThumbnail ? firstThumbnail.offsetWidth + 24 : 0
          dotsContainer.scrollTo({
            left: Math.max(0, button.offsetLeft - thumbnailWidth),
            behavior: 'smooth'
          })
        }
      } catch (_) {}
    },
    getDesignImage (designReference) {
      if (this.isWomenProductGroup) {
        return getImagePaths({
          s3: true,
          path: `/images/design-women/${designReference}/signup/size/${designReference}-${this.productGroupReference}`,
          noDesktop: true
        })
      }

      return getImagePaths({
        s3: true,
        path: `/images/design/${designReference}/products/${designReference}-${this.productGroup}${this.imagePostfix}`,
        noMobile: true
      })
    },
    thumbnailDesignImage (designReference) {
      if (this.isWomenProductGroup) {
        return getImagePaths({
          s3: true,
          path: `/images/design-women/${designReference}/products/${designReference}-${this.productGroupReference}`,
          noMobile: true
        })
      }

      return getImagePaths({
        s3: true,
        path: `/images/design/${designReference}/products/${designReference}-${this.productGroup}${this.imagePostfix}`,
        noMobile: true
      })
    },
    isActive (designReference) {
      return this.selectedDesignReference === designReference
    },
    updateFillBar (container) {
      if (!this.$refs.fillBar) { return }

      const scrollWidth = container.scrollWidth - container.clientWidth
      if (scrollWidth === 0) {
        this.$refs.fillBar.style.width = '0%'
        return
      }

      const scrollPercent = (container.scrollLeft / scrollWidth) * 100
      this.$refs.fillBar.style.width = `${scrollPercent}%`
    }
  }
}
</script>

<style scoped lang="postcss">
.design-picker {
  &-container {
    @apply rounded-rem border-neutral-200 border pt-9 relative;

    >>> img {
      @apply mx-auto;
    }

    &-shadow {
      @apply absolute bottom-0 right-0 left-0;
      background-color: #F1F2F7;
      height: 50%;
    }

    &-is-featured-label {
      @apply absolute pointer-events-none;
      top: 2rem;
      right: 2rem;

      .is-featured-label {
        border-radius: .6rem;
      }
    }
  }

  .arrow-previous,
  .arrow-next {
    &:focus {
      outline: 2px solid #36C0B6;
      outline-offset: 2px;
    }

    &:disabled {
      @apply opacity-50 cursor-not-allowed;
    }

    svg {
      top: 28%;
      left: 27%;
    }
  }

  .arrow-previous {
    left: 15px;
    top: 33%;
  }

  .arrow-next {
    right: 15px;
    top: 33%;
  }

  &-footer {
    @apply rounded-b-rem py-4 pl-5 pr-4;
  }

  .design-picker-container-cloak {
    @apply overflow-hidden border border-neutral-200;
    transition: opacity 0.3s ease-out;
  }

  .is-active-badge {
    @apply absolute;
    top: -2%;
    left: 56%;
    height: 2.6rem;
    width: 2.6rem;

    svg {
      @apply absolute;
      top: 27%;
      left: 27%;
    }
  }

  .scale-enter-active,
  .scale-leave-active {
    transition: all 0.2s ease;
  }

  .scale-enter,
  .scale-leave-to {
    transform: scale(0);
    opacity: 0;
  }

  .design-picker-disabled-notice {
    @apply absolute z-10 px-5 w-full;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    &-box {
      @apply p-5;
      background: rgba(249, 169, 30, 1);
      border-radius: .3rem;
    }
  }

  >>> .slick-dots {
    @apply flex overflow-x-scroll pt-5 pl-3 !important;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none;  /* IE 10+ */

    &::-webkit-scrollbar {
      display: none; /* Chrome/Safari/Webkit */
    }

    > li {
      @apply mr-3 flex-shrink-0;

      img {
        width: 7rem;
      }
    }
  }

  .filler-bar {
    border-top-right-radius: 99px;
    border-bottom-right-radius: 99px;
  }

  >>> .slick-slide .design-picker-container-cloak {
    @apply bg-neutral-200;
  }

  >>> .slick-slide.slick-active .design-picker-container-cloak {
    @apply opacity-0;
  }

  .disabled-button >>> p {
    @apply font-bold !important;
  }

  &.is-sliding {
    >>> .slick-slide .design-picker-container-cloak {
      @apply opacity-0;
    }
  }

  &.is-disabled {
    .thumbnail-button {
      @apply pointer-events-none cursor-default;
    }

    >>> .slick-dots {
      @apply overflow-hidden !important;

      li {
        @apply pointer-events-none;
      }
    }
  }

  &-women {
    .design-picker-container {
      @apply p-0;

      &-body {
        @apply p-0;

        >>> img {
          @apply w-full h-full object-cover;
        }
      }
    }
  }
}

@screen md {
  .design-picker {
    &.design-picker-women {
      >>> .slick-slide {
        .design-picker-container {
          max-height: 36rem;
        }
      }

      .design-picker-container-body {
        >>> img {
          max-height: 30.3rem;
        }
      }
    }
  }
}
</style>
